import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { EventEmitter } from 'events';
import { getDeviceFingerprint } from '@/utils/fingerprint';

export interface ApiResponse<T = any> {
  code: number;
  data: T;
  msg?: string;
  timestamp?: number;
}

export interface RefreshRequest {
  refresh_token: string;
}

export interface RefreshResponse {
	access_token: string;
	refresh_token: string;
	access_expires_at: string;
	refresh_expires_at: string;
}

const TOKEN_KEY = 'token';
export interface TokenInfo {
  access_token: string;
  refresh_token: string;
  access_expires_at: number;
  refresh_expires_at: number;
}

export const getToken = (): TokenInfo | null => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    return JSON.parse(token);
  }
  return null;
}

export const setToken = (token: TokenInfo): void => {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(token));
}

export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
}

export enum ApiErrorCode {
    Success = 0,
    ErrWithoutToken    = 4001, 
    ErrInvalidToken    = 4002, 
    ErrExpiredToken    = 4003, 
    ErrUserNotFound    = 4004, 
    ErrInvalidRequest  = 4005, 
    ErrWithoutDeviceID = 4006, 
    ErrDeviceNotActive = 4007, 
    ErrRateLimit       = 4008, 
    ErrTokenExpired    = 4009,
    ErrServerError     = 5001, 
}


export enum ApiEventType {
  UNAUTHORIZED = 'unauthorized',
  RATE_LIMIT = 'rate_limit',
  NETWORK_ERROR = 'network_error',
  SERVER_ERROR = 'server_error',
  UNKNOWN_ERROR = 'unknown_error',
  BUSINESS_ERROR = 'business_error',
  TOKEN_REFRESH = 'token_refresh',
  ERROR = 'error',
  MAINTENANCE = 'maintenance',
}


export interface WSKickMessage {
  Type: number;
  Time: number;
  IP: string;
}

export const ApiEventBus = new EventEmitter();
ApiEventBus.setMaxListeners(20);
export interface HttpServiceConfig {
  baseURL?: string;       
  timeout?: number;       
  autoRefreshToken?: boolean; 
}


export class HttpService {
  private client: AxiosInstance;
  private config: HttpServiceConfig;

  constructor(config: HttpServiceConfig = {}) {
    this.config = {
      baseURL: 'https://server.unmark.magicktools.com/api',
      //baseURL: "http://localhost:8000/api",
      timeout: 30000,
      autoRefreshToken: true,
      ...config
    };

    this.client = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      withCredentials: true,
    });

    this.client.interceptors.request.use(
      async (config) => {
        console.groupCollapsed(`%c API Request: %c${config.method} %c${config.url}`, 'color: blue; font-weight: bold;', 'color: orange; font-weight: bold;', 'color: black;');
        console.log('%c Headers:', 'color: gray;', config.headers);
        if (config.params) {
          console.log('%c Params:', 'color: gray;', config.params);
        }
        if (config.data) {
          console.log('%c Data:', 'color: gray;', config.data);
        }
        console.groupEnd();
        
        const token = getToken();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token.access_token}`;
        }
        
        try {
          const deviceId = await getDeviceFingerprint();
          if (deviceId) {
            config.headers['X-Device-ID'] = deviceId;
          } else {
            console.error('获取设备指纹失败，未能在请求头中设置 X-Device-ID');
          }
        } catch (error) {
          console.error('获取设备指纹时发生错误:', error);
        }
        return config;
      },
      (error) => {
        console.error("API Request Error:", error);
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      async (response) => {
        console.groupCollapsed(`%c API Response: %c${response.status} %c${response.config.method?.toUpperCase()} %c${response.config.url}`, 'color: green; font-weight: bold;', 'color: black;', 'color: orange; font-weight: bold;', 'color: black;');
        console.log('%c Data:', 'color: gray;', response.data);
        console.groupEnd();
      
        const data = response.data;
        if (data && data.code === ApiErrorCode.ErrExpiredToken) {
          const refreshed = await this.refreshToken();
          if (refreshed) {
            const token = getToken();
            if (token && response.config.headers) {
              response.config.headers['Authorization'] = `Bearer ${token.access_token}`;
            }
            if (response.config.headers) {
              try {
                const deviceId = await getDeviceFingerprint();
                if (deviceId) {
                  response.config.headers['X-Device-ID'] = deviceId;
                } else {
                  console.error('刷新token后，获取设备指纹失败，重试请求的 X-Device-ID 可能不正确');
                }
              } catch (error) {
                console.error('刷新token后，获取设备指纹时发生错误:', error);
              }
            }
            
            return this.client(response.config);
          }
        }

        if (data && data.code === ApiErrorCode.ErrWithoutDeviceID) {
          // 获取最新的设备指纹
          try {
            const deviceId = await getDeviceFingerprint();
            if (deviceId && response.config.headers) {
              response.config.headers['X-Device-ID'] = deviceId;
            } else if (response.config.headers) {
              console.error('ErrWithoutDeviceID: 获取设备指纹失败，重试请求的 X-Device-ID 可能不正确');
            }
          } catch (error) {
            console.error('ErrWithoutDeviceID: 获取设备指纹时发生错误:', error);
          }
          return this.client(response.config);
        }

        if (data && data.code === ApiErrorCode.ErrDeviceNotActive) {
          const msg = data.data as WSKickMessage;
          ApiEventBus.emit(ApiEventType.UNAUTHORIZED, {
            message: `${' Your account has been logged in on another device at ' + (new Date(msg.Time).toLocaleString()) + ' (IP: ' + msg.IP + ')' }`,
            requireLogin: true,
            reason: 'device_inactive',
            details: msg
          });
        }

        if (data && data.code === ApiErrorCode.ErrInvalidRequest) {
          ApiEventBus.emit(ApiEventType.ERROR, {
            message: data.msg,
            requireLogin: false
          });
        }

        if (data && data.code === ApiErrorCode.ErrUserNotFound) {
          ApiEventBus.emit(ApiEventType.ERROR, {
            message: data.msg,
            requireLogin: true
          });
        }

        if (data && data.code === ApiErrorCode.ErrWithoutToken) {
          // 获取最新的token

          const token = getToken();
          if (token && response.config.headers) {
            response.config.headers['Authorization'] = `Bearer ${token.access_token}`;
          }
          return this.client(response.config);
        }

        if (data && (data.code === ApiErrorCode.ErrInvalidToken || data.code === ApiErrorCode.ErrWithoutToken)) {
          ApiEventBus.emit(ApiEventType.UNAUTHORIZED, {
            message: data.msg,
            requireLogin: true
          });
        } else if (data && data.code === ApiErrorCode.ErrRateLimit) {
          ApiEventBus.emit(ApiEventType.RATE_LIMIT, {
            message: "your request is too frequent, please try again later"
          });
        } else if (data && data.code === ApiErrorCode.ErrServerError) {
          ApiEventBus.emit(ApiEventType.SERVER_ERROR, {
            message: "server error"
          });
        }
        return response;
      },
      async (error: AxiosError) => {
        console.error('API Response Error:', error);
        if (error.response) {
          const status = error.response.status;
          
          console.groupCollapsed(`%c API Error: %c${status} %c${error.config?.method?.toUpperCase()} %c${error.config?.url}`, 'color: red; font-weight: bold;', 'color: black;', 'color: orange; font-weight: bold;', 'color: black;');
          console.log('%c Response:', 'color: gray;', error.response);
          console.log('%c Error:', 'color: gray;', error);
          console.groupEnd();
          
        } else if (error.request) {
          ApiEventBus.emit(ApiEventType.NETWORK_ERROR, {
            message: 'network error, please check your network settings'
          });
        } else {
          ApiEventBus.emit(ApiEventType.ERROR, {
            message: 'request error: ' + error.message
          });
        }
        
        return Promise.reject(error);
      }
    );
  }

  public async refreshToken(): Promise<boolean> {
    const token = getToken();
    if (!token || !token.refresh_token) {
      return false;
    }
    const request = {
      refresh_token: token.refresh_token
    }
    const response = await this.post('/auth/refresh', request);
    if (response.code === 0 && response.data) {
      setToken(response.data);
      return true;
    } else {
      removeToken();
      return false;
    }
  }

  public getBaseUrl(): string {
    const baseURL = this.config.baseURL || '';
    if (baseURL.startsWith('/')) {
      return `${window.location.origin}${baseURL}`;
    }
    if (baseURL.startsWith('http')) {
      return baseURL;
    }
    return window.location.origin;
  }

  public async get<T = any>(url: string, params?: any): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url, { params });
    return response.data;
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  public async upload<T = any>(url: string, file: File, onProgress?: (percentage: number) => void, extraData?: Record<string, any>): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (extraData) {
      Object.entries(extraData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }
    
    const response = await this.client.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentage);
        }
      }
    });
    
    return response.data;
  }
} 